import { createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useAction, useMutation, useQuery } from "convex/react";
import { toast } from "sonner";
import { api } from "@/convex/_generated/api";
import { Id } from "@/convex/_generated/dataModel";
import { useCrdtDraft } from "@/hooks/useCrdtDraft";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

/**
 * Playground E2E para la infraestructura CRDT (Fase 2).
 *
 * Sólo accesible en dev (`import.meta.env.DEV`). En prod se redirige a /.
 * Usa la sesión real del deploy de dev: el usuario activo + su empresa +
 * sus permisos sobre `quotations`.
 *
 * Flujo:
 *   1. Genera un `docId` (uuid) al montar.
 *   2. `useCrdtDraft` mantiene el doc Automerge local + push/pull automático.
 *   3. Panel HEADER: inputs para editar cabecera → `change()` → push debounced.
 *   4. Panel ITEMS: agregar/quitar ítems → push.
 *   5. Botón COMMIT: llama `commitDraftAction` con el `containerId`.
 *   6. Panel RESULTADO: status, validationErrors, totals recalculados.
 */
export const Route = createFileRoute("/_authenticated/_dev/crdt-playground")(
  {
    beforeLoad: () => {
      if (!import.meta.env.DEV) {
        throw redirect({ to: "/" });
      }
    },
    component: CrdtPlayground,
  },
);

// Lo que va en el doc CRDT (shape de negocio que ve el server).
type ItemInDoc = {
  _kind: "new";
  name: string;
  quantity: number;
  unitPrice: number;
  unit?: string;
  taxRate?: number;
  sortOrder: number;
};

// Lo que ve el form: item del doc + id local de UI (NO va al server).
type Item = { localId: string } & ItemInDoc;

type QuotationDraftShape = {
  entityId?: string;
  header: {
    title?: string;
    description?: string;
    currency?: string;
    paymentTerms?: string;
    notes?: string;
  };
  items: ItemInDoc[];
};

const DEFAULT_DOC: QuotationDraftShape = {
  header: { currency: "HNL" },
  items: [],
};

function genDocId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `doc-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function genLocalId(): string {
  return `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function CrdtPlayground() {
  const [docId] = useState(() => genDocId());

  return (
    <div className="container mx-auto max-w-5xl space-y-4 py-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">CRDT Playground</h1>
        <p className="text-sm text-muted-foreground">
          Dev only. Sesión real, empresa real, permisos reales.
        </p>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">docId:</span>
          <code className="rounded bg-muted px-2 py-1 font-mono">{docId}</code>
        </div>
      </header>

      <DraftBoard docId={docId} />
    </div>
  );
}

function DraftBoard({ docId }: { docId: string }) {
  const draft = useCrdtDraft<QuotationDraftShape>({
    docId,
    kind: "quotation_draft",
    initialValue: DEFAULT_DOC,
    debounceMs: 800,
  });

  const commit = useAction(api.companies.crdt.commit.commitDraftAction);
  const createQuotationDraft = useMutation(
    api.companies.quotations.mutations.createQuotationDraft,
  );

  // pullChanges devuelve el containerId si el doc existe en el server.
  // Lo usamos para llamar al commit. Si todavía no hay contenedor (no se hizo
  // push), forzamos un flush y volvemos a chequear.
  const remote = useQuery(api.companies.crdt.queries.pullChanges, {
    docId,
    kind: "quotation_draft",
  });

  const [commitResult, setCommitResult] = useState<
    | { ok: true; entityId: string }
    | { ok: false; errors: string[] }
    | null
  >(null);
  const [committing, setCommitting] = useState(false);

  // Crear la fila de cotización en el server (auth + permisos via secureMutation).
  // Devuelve el entityId; lo guardamos en el doc CRDT para que el commit lo vea.
  const ensureEntity = async (): Promise<string | null> => {
    if (draft.value?.entityId) return draft.value.entityId;
    const { quotationId } = await createQuotationDraft({
      currency: draft.value?.header.currency ?? "HNL",
    });
    draft.change((d) => {
      d.entityId = quotationId as string;
    });
    await draft.flush();
    return quotationId as string;
  };

  const handleCommit = async () => {
    setCommitting(true);
    setCommitResult(null);
    try {
      await ensureEntity();
      // Re-leer remote: ahora el push anterior ya debería haber llegado.
      await draft.flush();
      const containerId = remote?.containerId as
        | Id<"crdt_documents">
        | undefined;
      if (!containerId) {
        setCommitResult({
          ok: false,
          errors: [
            "El contenedor todavía no existe en el server. Esperá unos segundos y reintentá.",
          ],
        });
        return;
      }

      const res = await commit({
        containerId,
        kind: "quotation_draft",
      });
      setCommitResult(res);

      if (res.ok) {
        toast.success(`Confirmado: ${res.entityId}`);
      } else {
        toast.error(res.errors[0] ?? "Error en el commit");
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setCommitResult({ ok: false, errors: [msg] });
      toast.error(msg);
    } finally {
      setCommitting(false);
    }
  };

  // Auto-merge: si el remote tiene entityId pero el doc local no, lo adoptamos.
  useEffect(() => {
    if (remote?.entityId && !draft.value?.entityId) {
      draft.change((d) => {
        d.entityId = remote.entityId;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remote]);

  return (
    <Tabs defaultValue="edit">
      <TabsList>
        <TabsTrigger value="edit">Editar</TabsTrigger>
        <TabsTrigger value="state">Estado</TabsTrigger>
        <TabsTrigger value="commit">Commit</TabsTrigger>
      </TabsList>

      <TabsContent value="edit" className="space-y-4">
        <HeaderEditor
          value={draft.value?.header ?? {}}
          onChange={(header) =>
            draft.change((d) => {
              d.header = header;
            })
          }
        />
        <ItemsEditor
          items={(draft.value?.items ?? []).map((it, idx) => ({
            ...it,
            localId: `${idx}`,
          }))}
          onChange={(items) =>
            draft.change((d) => {
              // Strip `localId`: es metadata de UI (clave de React) — no es
              // parte del shape de negocio que el server espera. Antes se
              // colaba acá y rompía el validador estricto de args.
              d.items = items.map(({ localId: _localId, ...rest }) => rest);
            })
          }
        />
      </TabsContent>

      <TabsContent value="state" className="space-y-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Estado del hook</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <Row label="saveState" value={draft.saveState} />
            <Row
              label="lastSavedAt"
              value={
                draft.lastSavedAt
                  ? new Date(draft.lastSavedAt).toLocaleTimeString()
                  : "—"
              }
            />
            <Row
              label="hasPendingChanges"
              value={String(draft.hasPendingChanges)}
            />
            <Row label="status" value={draft.status} />
            <Row label="isOffline" value={String(draft.isOffline)} />
            <Row
              label="validationErrors"
              value={draft.validationErrors.join(" | ") || "—"}
            />
            <Row
              label="containerId"
              value={remote?.containerId ?? "—"}
            />
            <Row label="entityId" value={draft.value?.entityId ?? "—"} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Doc materializado</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="overflow-auto rounded bg-muted p-3 text-xs">
              {JSON.stringify(draft.value, null, 2)}
            </pre>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="commit" className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Confirmar borrador</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              El commit toma el lock, carga los chunks, valida contra el
              validador de cotización, y aplica cabecera + ítems via las
              mutations existentes.
            </p>
            <Button
              onClick={() => void handleCommit()}
              disabled={committing}
            >
              {committing ? "Confirmando…" : "Confirmar (commit)"}
            </Button>
            {commitResult && (
              <div
                className={`rounded border p-3 text-sm ${
                  commitResult.ok
                    ? "border-green-500 bg-green-50 dark:bg-green-950"
                    : "border-red-500 bg-red-50 dark:bg-red-950"
                }`}
              >
                <div className="font-medium">
                  {commitResult.ok ? "OK" : "Error"}
                </div>
                {commitResult.ok ? (
                  <div>entityId: {commitResult.entityId}</div>
                ) : (
                  <ul className="list-disc pl-5">
                    {commitResult.errors.map((e, i) => (
                      <li key={i}>{e}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}

function HeaderEditor({
  value,
  onChange,
}: {
  value: QuotationDraftShape["header"];
  onChange: (next: QuotationDraftShape["header"]) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Cabecera</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-3 sm:grid-cols-2">
        <Field
          label="Título"
          value={value.title ?? ""}
          onChange={(v) => onChange({ ...value, title: v })}
        />
        <Field
          label="Descripción"
          value={value.description ?? ""}
          onChange={(v) => onChange({ ...value, description: v })}
        />
        <Field
          label="Moneda"
          value={value.currency ?? "HNL"}
          onChange={(v) => onChange({ ...value, currency: v })}
        />
        <Field
          label="Condiciones de pago"
          value={value.paymentTerms ?? ""}
          onChange={(v) => onChange({ ...value, paymentTerms: v })}
        />
        <Field
          label="Notas"
          value={value.notes ?? ""}
          onChange={(v) => onChange({ ...value, notes: v })}
        />
      </CardContent>
    </Card>
  );
}

function ItemsEditor({
  items,
  onChange,
}: {
  items: Item[];
  onChange: (next: Item[]) => void;
}) {
  const add = () => {
    onChange([
      ...items,
      {
        localId: genLocalId(),
        _kind: "new" as const,
        name: "",
        quantity: 1,
        unitPrice: 0,
        unit: "unidad",
        taxRate: 15,
        sortOrder: items.length + 1,
      },
    ]);
  };

  const updateOne = (idx: number, patch: Partial<Item>) => {
    onChange(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };

  const remove = (idx: number) => {
    onChange(items.filter((_, i) => i !== idx));
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">Ítems</CardTitle>
        <Button size="sm" variant="outline" onClick={add}>
          + Agregar
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        {items.length === 0 && (
          <p className="text-sm text-muted-foreground">Sin ítems.</p>
        )}
        {items.map((it, idx) => (
          <div
            key={it.localId}
            className="grid gap-2 rounded border p-3 sm:grid-cols-12"
          >
            <Field
              label="Nombre"
              className="sm:col-span-4"
              value={it.name}
              onChange={(v) => updateOne(idx, { name: v })}
            />
            <Field
              label="Cantidad"
              type="number"
              className="sm:col-span-2"
              value={String(it.quantity)}
              onChange={(v) => updateOne(idx, { quantity: Number(v) })}
            />
            <Field
              label="Precio unitario"
              type="number"
              className="sm:col-span-3"
              value={String(it.unitPrice)}
              onChange={(v) => updateOne(idx, { unitPrice: Number(v) })}
            />
            <Field
              label="ISV %"
              type="number"
              className="sm:col-span-2"
              value={String(it.taxRate ?? 0)}
              onChange={(v) => updateOne(idx, { taxRate: Number(v) })}
            />
            <div className="flex items-end sm:col-span-1">
              <Button
                size="icon"
                variant="ghost"
                onClick={() => remove(idx)}
                aria-label="Eliminar"
              >
                ×
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  className = "",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  className?: string;
}) {
  const id = useMemo(
    () => `f-${Math.random().toString(36).slice(2, 8)}`,
    [],
  );
  return (
    <div className={`space-y-1 ${className}`}>
      <Label htmlFor={id} className="text-xs">
        {label}
      </Label>
      <Input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}